import React from "react";
import { Avatar, Box, Button, Link, Tooltip, Typography } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { C } from "../../../common/theme";
import { formatAge } from "../../../common/utils/format";
import { SalesOrder } from "../../../common/requests/sales/endpoints";
import { WELCOME_VOUCHER, feeNote, minusMoney } from "../../../common/utils/bringBuyers";
import { Banner, CopyValue, DetailDialog, DetailList, DetailSection, EmptyState, StatusPill } from "../../../ui";
import { OrderGroup, SaleLine, attentionReason, formatDate, formatMoney, statusRank } from "../salesData";
import { statusTone } from "../orderView";
import { GroupStatus, Tracking, timeLabel } from "./OrdersTable";

interface OrderDetailDialogProps {
  open: boolean;
  /** The id from the URL, shown in the not-found state */
  orderId: string | null;
  loading: boolean;
  /** undefined = not in the loaded data */
  found: { order: SalesOrder; group: OrderGroup | null } | undefined;
  reportingCurrency: string;
  showIdentity: boolean;
  canComplete: boolean;
  onClose: () => void;
  onComplete: (order: SalesOrder) => void;
  onCancel: (order: SalesOrder) => void;
}

const userUrl = (username: string) => `https://inflos.co/u/${encodeURIComponent(username)}`;

function UserLink({ username }: { username: string | null }) {
  if (!username) return <>–</>;
  return (
    <Link href={userUrl(username)} target="_blank" rel="noopener noreferrer" underline="hover">
      {username}
    </Link>
  );
}

function ItemBlock({ line, currency, reportingCurrency, showIdentity }: { line: SaleLine; currency: string; reportingCurrency: string; showIdentity: boolean }) {
  const reason = attentionReason(line);
  const breakdown = [
    `${formatMoney(line.unit_price, currency)} × ${line.quantity}`,
    line.delivery ? `+ ${formatMoney(line.delivery, currency)} delivery` : null,
    line.discount ? `− ${formatMoney(line.discount, currency)} discount` : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", p: 1.5, display: "flex", flexDirection: "column", gap: 1.5, bgcolor: reason ? C.bone1 : C.paper }}>
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Avatar variant="rounded" src={line.image ?? undefined} alt={line.name ?? ""} sx={{ width: 40, height: 40, fontSize: 13, bgcolor: C.bone1, color: C.ink2 }}>
          {(line.name ?? "?").slice(0, 1)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {line.name ?? "Unnamed listing"}
            {line.is_special_offer && (
              <Tooltip title="Bought through a special offer">
                <LocalOfferOutlinedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", color: C.muted }} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {[line.genus, showIdentity && line.seller ? `seller ${line.seller}` : null].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <StatusPill tone={statusTone(line.status)}>{line.status}</StatusPill>
      </Box>
      {reason && (
        <Typography variant="caption" sx={{ color: C.coralText, fontWeight: 600 }}>
          Needs attention: {reason}
        </Typography>
      )}
      <DetailList
        columns={3}
        items={[
          {
            label: "Net",
            value: (
              <>
                <Box component="span" sx={{ fontWeight: 700, textDecoration: line.counts_toward_revenue ? undefined : "line-through" }}>
                  {formatMoney(line.net, currency)}
                </Box>
                <Typography variant="caption" sx={{ color: C.muted, display: "block" }}>
                  {breakdown}
                </Typography>
              </>
            ),
          },
          {
            label: "Platform fee",
            value: (
              <>
                {formatMoney(line.platform_fee_reporting, reportingCurrency)}
                {feeNote(line.fee_reason) && (
                  <Typography variant="caption" sx={{ color: C.muted, display: "block" }}>
                    {feeNote(line.fee_reason)}
                  </Typography>
                )}
              </>
            ),
          },
          { label: "Tracking", value: <Tracking line={line} /> },
        ]}
      />
    </Box>
  );
}

/** One order: who, when, every item, and the Complete / Cancel actions.
 * Opened from the table or straight from `/sales?order=<id>`. */
export default function OrderDetailDialog({
  open,
  orderId,
  loading,
  found,
  reportingCurrency,
  showIdentity,
  canComplete,
  onClose,
  onComplete,
  onCancel,
}: OrderDetailDialogProps) {
  if (!open) return null;
  if (loading || !found) {
    return (
      <DetailDialog open onClose={onClose} title="Order" size="md" loading={loading}>
        <EmptyState
          icon={<SearchOffIcon />}
          title="Order not found"
          body={
            <>
              No order with id <span className="mono">{orderId?.trim()}</span> in the sales data. It may be unpaid, or the link
              may be wrong.
            </>
          }
          action={
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          }
        />
      </DetailDialog>
    );
  }

  const { order, group } = found;
  const createdAt = group?.createdAt ?? new Date(order.created_at);
  const lines = (group?.lines ?? []).slice().sort((a, b) => statusRank(a.status) - statusRank(b.status));
  const first = lines[0];

  const actions =
    canComplete && group && (group.completable || group.refundable) ? (
      <>
        {group.refundable && (
          <Button variant="contained" color="secondary" onClick={() => onCancel(order)}>
            Cancel and refund
          </Button>
        )}
        {group.completable && (
          <Button variant="contained" onClick={() => onComplete(order)}>
            Mark completed
          </Button>
        )}
      </>
    ) : undefined;

  return (
    <DetailDialog
      open
      onClose={onClose}
      size="md"
      title={`Order ${order.order_id.slice(0, 8)}`}
      subtitle={
        first
          ? `${first.name ?? "Unnamed listing"}${lines.length > 1 ? ` +${lines.length - 1} more` : ""} · ${formatDate(createdAt)}`
          : formatDate(createdAt)
      }
      status={
        <>
          {group && <GroupStatus group={group} inline />}
          {order.is_manual && <StatusPill tone="outline">Manual</StatusPill>}
        </>
      }
      actions={actions}
    >
      {group && group.attention.length > 0 && (
        <Banner tone="warning" title="Needs attention.">
          {group.attention.join(" · ")}
        </Banner>
      )}
      {order.fx_missing && (
        <Banner tone="warning">
          No exchange rate on file for {order.currency}; this order is left out of {reportingCurrency} totals.
        </Banner>
      )}

      <DetailSection title="Order">
        <DetailList
          items={[
            { label: "Order ID", value: <CopyValue value={order.order_id} mono />, full: true },
            {
              label: "Placed",
              value: (
                <>
                  {formatDate(createdAt)}, {timeLabel(createdAt)}
                  <Box component="span" sx={{ color: C.muted }}>
                    {" "}
                    · {formatAge(createdAt)}
                  </Box>
                </>
              ),
            },
            {
              label: "Total",
              value: group ? (
                <>
                  {formatMoney(group.net, order.currency)}
                  {order.currency !== reportingCurrency && (
                    <Box component="span" sx={{ color: C.muted }}>
                      {" "}
                      ≈ {formatMoney(group.netReporting, reportingCurrency)}
                    </Box>
                  )}
                </>
              ) : (
                "–"
              ),
            },
            {
              label: "Buyer",
              value: (
                <>
                  {showIdentity ? <UserLink username={order.buyer} /> : "Hidden"}
                  {order.is_first_order ? " · first order" : ""}
                </>
              ),
            },
            {
              label: "Ships to",
              value: `${order.buyer_country ?? "–"} · ${order.is_manual ? "not paid (manual order)" : `paid in ${order.currency}`}`,
            },
            ...(order.vouchers?.length
              ? [
                  {
                    label: WELCOME_VOUCHER,
                    value: order.vouchers
                      .map((v) => `${minusMoney(v.amount, v.currency)} · paid by ${showIdentity && v.seller ? v.seller : "the seller"}`)
                      .join(", "),
                  },
                ]
              : []),
            ...(order.is_manual ? [{ label: "Created without payment", value: order.created_by ?? "From this dashboard" }] : []),
          ]}
        />
      </DetailSection>

      <DetailSection title={`Items (${lines.length})`}>
        {lines.length === 0 ? (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No items on this order.
          </Typography>
        ) : (
          lines.map((l) => (
            <ItemBlock key={l.order_item_id} line={l} currency={order.currency} reportingCurrency={reportingCurrency} showIdentity={showIdentity} />
          ))
        )}
      </DetailSection>
    </DetailDialog>
  );
}
